import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackEvent } from '../lib/analytics';
import { trackProductEvent } from '../lib/productEvents';


function PricingPage({ session }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [pricingStatus, setPricingStatus] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);

  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';

  useEffect(() => {
    trackEvent('pricing_opened');

    void trackProductEvent({
      userId: session?.user?.id,
      eventName: 'pricing_opened',
    });
  }, [session?.user?.id]);

  useEffect(() => {
    const loadPricingStatus = async () => {
      try {
        const response = await fetch(
          process.env.REACT_APP_GET_PRICING_STATUS_URL
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load pricing status.');
        }

        setPricingStatus(data);
      } catch (err) {
        console.error(err);

        /*
          Frontend fallback only.
          The backend checkout function still decides the actual Stripe price.
        */
        setPricingStatus({
          foundingPriceAvailable: true,
          monthlyPrice: 29,
          priceTier: 'founder',
          spotsRemaining: null,
        });
      } finally {
        setPricingLoading(false);
      }
    };

    loadPricingStatus();
  }, []);

  const foundingPriceAvailable =
    pricingStatus?.foundingPriceAvailable !== false;

  const monthlyPrice = pricingStatus?.monthlyPrice || 29;

  const ctaText = foundingPriceAvailable
    ? 'Lock In Founding Member Price'
    : 'Upgrade to Pro';

  const badgeText = foundingPriceAvailable
    ? 'Founding Member Price'
    : 'StockVested Pro';

  const titleText = foundingPriceAvailable
    ? 'Lock in your founding member price'
    : 'Upgrade your trading workflow';

  const subtitleText = foundingPriceAvailable
    ? 'Get unlimited analyzer snapshots, private journaling, and performance tracking at the early-access member rate.'
    : 'Unlock unlimited analyzer snapshots, private journaling, and performance tracking inside your AI-powered research workflow.';

  const handleStartCheckout = async () => {
    setLoading(true);
    setError('');

    try {
      const accessToken = session?.access_token;

      trackEvent('checkout_started', {
        displayed_price: monthlyPrice,
        displayed_price_tier: pricingStatus?.priceTier || 'unknown',
        founding_price_available: foundingPriceAvailable,
      });

      void trackProductEvent({
        userId: session?.user?.id,
        eventName: 'checkout_started',
        metadata: {
          displayed_price: monthlyPrice,
          displayed_price_tier:
            pricingStatus?.priceTier || 'unknown',

          founding_price_available:
            foundingPriceAvailable,
        },
      });

      const response = await fetch(process.env.REACT_APP_CREATE_CHECKOUT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        if (
          response.status === 409 &&
          data.code === 'SUBSCRIPTION_ALREADY_ACTIVE'
        ) {
          throw new Error(
            'You already have an active subscription. Use Manage Billing instead.'
          );
        }

        throw new Error(data.error || 'Failed to create checkout session.');
      }

      if (!data.url) {
        throw new Error('No checkout URL returned.');
      }

      window.location.href = data.url;
    } catch (err) {
      void trackProductEvent({
        userId: session?.user?.id,
        eventName: 'checkout_creation_failed',
        metadata: {
          displayed_price: monthlyPrice,
          displayed_price_tier:
            pricingStatus?.priceTier || 'unknown',
        },
      });
      console.error(err);
      setError(err.message || 'Unable to start checkout.');
    } finally {
      setLoading(false);
    }
  };

  if (pricingLoading) {
  return (
    <div className="app dark mobile-safe-page">
      <main className="pricing-page">
        <div className="pricing-shell">
          <button
            type="button"
            className="back-link-btn"
            onClick={() => navigate('/')}
          >
            ← Back to Analyzer
          </button>

          <div className="pricing-card">
            <div className="app-initial-loader">
              <div className="inline-analysis-spinner" />
              <p className="inline-analysis-title">Loading pricing...</p>
              <p className="inline-analysis-subtext">
                Checking current founding member availability
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

  return (
    <div className="app dark mobile-safe-page">
      <main className="pricing-page">
        <div className="pricing-shell">
          <button
            type="button"
            className="back-link-btn"
            onClick={() => navigate('/')}
          >
            ← Back to Analyzer
          </button>

          <div className="pricing-card">
            <div className="pricing-badge">{badgeText}</div>

            <h1 className="pricing-title">{titleText}</h1>

            <p className="pricing-subtitle">{subtitleText}</p>

            <div className="pricing-price-row">
              <span className="pricing-price">${monthlyPrice}</span>
              <span className="pricing-price-suffix">/month</span>
            </div>

            {foundingPriceAvailable && !pricingLoading && (
              <div className="pricing-founder-note">
                <strong>Limited-time early access pricing.</strong>
                <span>
                  Lock in $29/month as a founding member. Future customers may
                  see higher pricing, but early members keep this rate as long
                  as their subscription remains active.
                  {Number.isFinite(pricingStatus?.spotsRemaining)
                    ? ` ${pricingStatus.spotsRemaining} founding spots remaining.`
                    : ''}
                </span>
              </div>
            )}

            {!foundingPriceAvailable && !pricingLoading && (
              <div className="pricing-founder-note">
                <strong>Standard Pro pricing.</strong>
                <span>
                  Founding member pricing has ended. Pro access is now available
                  at the standard monthly rate.
                </span>
              </div>
            )}

            <ul className="pricing-features">
              <li>Unlimited AI-powered chart research snapshots</li>
              <li>Support, resistance, and structural level mapping</li>
              <li>Persistent private analysis history</li>
              <li>Journal and performance tracking workflow</li>
              <li>Priority access to future pro features</li>
            </ul>

            <button
              type="button"
              className="upgrade-btn primary pricing-cta"
              onClick={handleStartCheckout}
              disabled={loading}
            >
              {loading ? 'Redirecting...' : ctaText}
            </button>

            <button
              type="button"
              className="pricing-secondary-btn"
              onClick={() => navigate('/')}
            >
              Maybe later
            </button>

            {checkoutCancelled && (
              <p className="usage-error">
                Checkout was cancelled. Your plan has not been changed.
              </p>
            )}

            {error && <p className="error">{error}</p>}
          </div>
        </div>
      </main>
    </div>
  );
}

export default PricingPage;